'use client'

import { useEffect, useRef, useState } from 'react'

/**
 * useIsAnyElementVisible
 *
 * Accepts an array of CSS selectors (strings). Returns **true** if at least
 * one element matching any of the selectors is **currently visible in the
 * viewport** according to an `IntersectionObserver`.
 *
 * This version replaces manual `getBoundingClientRect` scroll/resize polling
 * with an `IntersectionObserver`, eliminating repeated `document.querySelectorAll`
 * calls in event handlers and providing more reliable, performant visibility
 * detection that is friendly to React's lifecycle.
 *
 * The initial `querySelectorAll` call inside `useEffect` is acceptable because
 * it runs client-side only and the resulting elements are observed via refs
 * rather than being queried on every frame.
 *
 * @param {string[]} selectors - Array of CSS selectors to look for.
 * @returns {boolean} Whether any matching element is visible.
 */
export default function useIsAnyElementVisible(selectors = []) {
  const [isVisible, setIsVisible] = useState(false)
  const observedElementsRef = useRef([])

  useEffect(() => {
    if (!Array.isArray(selectors) || selectors.length === 0) {
      setIsVisible(false)
      return
    }

    // Collect all matching elements once
    const elements = selectors.flatMap((selector) =>
      Array.from(document.querySelectorAll(selector))
    )
    observedElementsRef.current = elements

    if (elements.length === 0) {
      setIsVisible(false)
      return
    }

    // Track per-element intersection state so we can derive the "any" value
    const visibilityMap = new Map(elements.map((el) => [el, false]))

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          visibilityMap.set(entry.target, entry.isIntersecting)
        })

        const anyVisible = Array.from(visibilityMap.values()).some(Boolean)
        setIsVisible(anyVisible)
      },
      { threshold: 0 }
    )

    elements.forEach((el) => observer.observe(el))

    return () => {
      observer.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(selectors)])

  return isVisible
}
